import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { useState, useEffect, useRef } from "react";
import "./CheckoutForm.css";
import { useNavigate } from "react-router-dom";

// How long we wait before telling the user their connection looks slow while
// Stripe confirms the payment. We never abort this call ourselves — doing so
// mid-confirmation could leave the charge in an ambiguous state — we only
// change the message shown while we keep waiting for Stripe's own response.
const CONFIRM_SLOW_THRESHOLD_MS = 6000;

// Prefill the billing country as Singapore instead of Stripe's IP-based guess.
// The user can still change it in the form.
const PAYMENT_ELEMENT_OPTIONS = {
  defaultValues: {
    billingDetails: {
      address: { country: "SG" },
    },
  },
};

const CheckoutForm = ({ onClose, onPaymentSuccess, planType }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [paid, setPaid] = useState(false);
  // Guards against double submits (state updates aren't instant, so a fast
  // double-click / Enter+click can get past the disabled button)
  const submittingRef = useRef(false);
  // PaymentIntent ids we've already reported to the parent. Stripe answers a
  // repeat confirm with `payment_intent_unexpected_state` (status succeeded),
  // and the 3DS redirect effect can re-run, so without this the parent's
  // purchase API could be called more than once for one payment.
  const reportedIntentsRef = useRef(new Set());

  // Calls onPaymentSuccess at most once per PaymentIntent
  const reportSuccess = async (pi) => {
    const key = pi?.id || "__no_id__";
    if (reportedIntentsRef.current.has(key)) {
      console.warn("onPaymentSuccess already called for this payment, skipping:", key);
      return;
    }
    reportedIntentsRef.current.add(key);
    await onPaymentSuccess(pi);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current || paid) return;
    submittingRef.current = true;
    setLoading(true);
    setMessage(null);

    if (!stripe || !elements) {
      submittingRef.current = false;
      setLoading(false);
      return;
    }

    let settled = false;
    const slowTimer = setTimeout(() => {
      if (!settled) {
        setMessage(
          "This is taking longer than usual — your connection may be slow. Please keep this window open while we confirm your payment..."
        );
      }
    }, CONFIRM_SLOW_THRESHOLD_MS);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        // return to the current page after any redirect (3DS) so we can
        // retrieve the PaymentIntent and continue the app flow.
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      settled = true;
      clearTimeout(slowTimer);

      if (error) {
        console.error("Payment error:", error);

        // Stripe may return `payment_intent_unexpected_state` when the
        // PaymentIntent has already been processed server-side.
        if (
          error.code === "payment_intent_unexpected_state" &&
          error.payment_intent &&
          (error.payment_intent.status === "succeeded" ||
            error.payment_intent.status === "processing")
        ) {
          const pi = error.payment_intent;
          console.warn("Payment intent already in terminal state, treating as success:", pi);
          setMessage("✅ Payment successful! Redirecting...");
          setPaid(true);
          try {
            await reportSuccess(pi);
          } catch (e) {
            console.error("onPaymentSuccess handler error:", e);
          }

          // ✅ Redirect to /chart after success
          setTimeout(() => {
            onClose();
            navigate("/chart");
          }, 1500);
        } else {
          setMessage(error.message || "Payment failed. Please try again.");
        }
      } else if (paymentIntent) {
        if (paymentIntent.status === "succeeded" || paymentIntent.status === "processing") {
          setMessage("✅ Payment successful! Redirecting...");
          setPaid(true);
          try {
            console.log("💳 Payment Intent details:", {
              id: paymentIntent.id,
              amount: paymentIntent.amount,
              currency: paymentIntent.currency,
              status: paymentIntent.status,
              payment_method: paymentIntent.payment_method,
              created: new Date(paymentIntent.created * 1000).toISOString(),
              metadata: paymentIntent.metadata
            });
            console.log("📋 Full Payment Intent object:", paymentIntent);
            
            await reportSuccess(paymentIntent);

            console.log("✅ onPaymentSuccess completed");
          } catch (e) {
            console.error("❌ onPaymentSuccess handler error:", e);
          }

          // ✅ Don't redirect - let Cart.jsx handle navigation
        } else {
          console.log("Payment status:", paymentIntent.status);
          setMessage(`⚠️ Payment status: ${paymentIntent.status}`);
        }
      }
    } catch (err) {
      console.error("Exception during payment confirmation:", err);
      setMessage(`Error: ${err.message || "Unknown error occurred"}`);
    } finally {
      settled = true;
      clearTimeout(slowTimer);
      submittingRef.current = false;
    }

    setLoading(false);
  };

  // Handle the case where Stripe redirected back to this page after
  // authentication (3DS). Stripe appends `payment_intent_client_secret`
  // to the URL. Retrieve the PaymentIntent and treat succeeded/processing
  // as payment success so the parent can finalize the purchase.
  useEffect(() => {
    if (!stripe) return;

    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get("payment_intent_client_secret");
    if (!clientSecret) return;

    let mounted = true;

    (async () => {
      try {
        const result = await stripe.retrievePaymentIntent(clientSecret);
        const pi = result?.paymentIntent;
        if (!mounted || !pi) return;

        if (pi.status === "succeeded" || pi.status === "processing") {
          setMessage("✅ Payment successful! Processing your order...");
          setPaid(true);

          // Clean up the URL first so a re-run of this effect (re-render,
          // StrictMode) can't process the same payment again
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete("payment_intent_client_secret");
            window.history.replaceState({}, document.title, url.toString());
          } catch (e) {
            // ignore
          }

          try {
            console.log("📦 Calling onPaymentSuccess handler from redirect...");
            console.log("💳 Payment Intent details:", {
              id: pi.id,
              amount: pi.amount,
              currency: pi.currency,
              status: pi.status,
              payment_method: pi.payment_method,
              created: new Date(pi.created * 1000).toISOString(),
              metadata: pi.metadata,
              customer: pi.customer,
              description: pi.description
            });
            console.log("📋 Full Payment Intent object:", pi);
            
            await reportSuccess(pi);

            console.log("✅ onPaymentSuccess handler called successfully");
            setMessage("✅ Payment successful! Your digital amulets have been delivered.");
          } catch (e) {
            console.error("❌ onPaymentSuccess handler error:", e);
            setMessage("⚠️ Payment received but order processing failed. Please contact support.");
          }
        } else {
          setMessage(`⚠️ Payment status: ${pi.status}`);
        }
      } catch (err) {
        console.error("Error retrieving PaymentIntent after redirect:", err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [stripe, onPaymentSuccess, onClose, navigate]);

  return (
    <div className="checkout-form">
      <form className="checkout-form__form" onSubmit={handleSubmit}>
        <div className="payment-element-wrapper">
          <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} />
        </div>
        <button
          className="submit-button"
          type="submit"
          disabled={!stripe || loading || paid}
          aria-label="Pay Now"
          aria-disabled={!stripe || loading || paid}
        >
          {paid ? "Paid" : loading ? "Processing..." : "Pay Now"}
        </button>
      </form>
      {message && <p className="checkout-message">{message}</p>}
    </div>
  );
};

export default CheckoutForm;